import React, { useState, useEffect } from 'react';
import './BuyerMarketplace.scss';
import { Breadcrumb, BreadcrumbItem, Card } from 'reactstrap';
import { HomeButton } from '../../../components/HomeButton/home-button';
import Layout from '../../../components/Layout';
import MarketplaceOpportunities from './MarketplaceOpportunities/MarketplaceOpportunities';
import BuyerProfile from './BuyerProfile/BuyerProfile';
import { OpportunityDetails } from './OpportunityDetails/OpportunityDetails';
import { useStateSelector } from '../../../store/selectors';
import { GetBuyer } from '../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { useDispatch } from 'react-redux';
import ContactSupportButton from '../../ContactSupportButton/ContactSupportButton';
import { SupportContextEnum } from '../../../support-portal/models/SupportContext';
import MasterDeadlineDocket from './MasterDeadlineDocket/MasterDeadlineDocket';
import { MyTeamMembers } from './MyTeamMembers/MyTeamMembers';
import ConnectMarketplaceAgreements from './ConnectMarketplaceAgreements/ConnectMarketplaceAgreements';
import ConnectMarketplaceTemplates from './ConnectMarketplaceTemplates/ConnectMarketplaceTemplates';
import CurrentProcessCommercialTerms from './CurrentProcessCommercialTerms/CurrentProcessCommercialTerms';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { USER_ROLES } from '../../../utils/constants';
import { FeatureFlagsEnum } from '../../FeatureFlags/FeatureFlags.enum';
import { useFeatureFlag } from '../../../custom-hooks/useFeatureFlag';
import { BuyerMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/buyer-marketplace-tab.enum';
import {
    TeamMemberPermissions,
    TeamMemberPermissionsHelper,
} from './MyTeamMembers/TeamMemberPermissionsHelper';
import {
    TeamMemberType,
    InternalTeamMemberRole,
} from './MyTeamMembers/Models/Enums';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { RefreshNotificationFlags } from '../../../slices/notification-flags/refresh-notification-flags';
import { BuyerNdaGate } from './BuyerNdaGate/BuyerNdaGate';
import { BuyersNdaStatusResponse } from '../../../slices/marketplace/timeline/models/buyers-nda-status.model';

const BuyerMarketplace = () => {
    const [activeTab, setActiveTab] = useState(
        BuyerMarketplaceTab.Opportunities
    );
    const isFF_MrktplcBuyers_3_6Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_6
    );
    const [searchParams] = useSearchParams();
    const [refreshOpportunitiesToggle, setRefreshOpportunitiesToggle] =
        useState(false);
    const navigate = useNavigate();
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );
    const axios = useStateSelector((state) => state.core.axios);
    const [isNdaSigned, setIsNdaSigned] = useState(false);
    const [isNdaCheckCompleted, setIsNdaCheckCompleted] = useState(false);
    const tabComponents = [
        {
            id: BuyerMarketplaceTab.Opportunities,
            label: 'Marketplace Opportunities',
            content: (
                <MarketplaceOpportunities
                    refreshOpportunitiesToggle={refreshOpportunitiesToggle}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}`,
        },
        {
            id: BuyerMarketplaceTab.DeadlineDocket,
            label: 'Deadline Docket',
            content: (
                <MasterDeadlineDocket
                    setActiveTab={setActiveTab}
                    selectedOpportunityId={selectedOpportunity.opportunityId}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.MasterDeadlineDocketCode}`,
        },
        {
            id: BuyerMarketplaceTab.Profile,
            label: 'Buyer Profile',
            content: <BuyerProfile />,
        },
        {
            id: BuyerMarketplaceTab.MyTeamMembers,
            label: 'My Team Members',
            content: <MyTeamMembers />,
        },
        {
            id: BuyerMarketplaceTab.ConnectMarketplaceAgreements,
            label: (
                <span>
                    <i>Connect</i> Agreements
                </span>
            ),
            content: <ConnectMarketplaceAgreements />,
        },
        {
            id: BuyerMarketplaceTab.ConnectMarketplaceTemplates,
            label: (
                <span>
                    <i>Connect</i> Document Templates
                </span>
            ),
            content: <ConnectMarketplaceTemplates />,
        },
        {
            id: BuyerMarketplaceTab.CurrentProcessCommercialTerms,
            label: (
                <span>
                    <i>Connect</i> Fee Schedule
                </span>
            ),
            content: <CurrentProcessCommercialTerms showDateModified={true} />,
        },
    ];
    const dispatch = useDispatch() as any;

    const fetchBuyerProfile = async () => {
        await dispatch(GetBuyer(null));
    };
    const buyerProfile = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    useEffect(() => {
        const tabParam = searchParams.get('tab');

        if (tabParam === 'my-team-members') {
            setActiveTab(BuyerMarketplaceTab.MyTeamMembers);
            navigate('/opportunities', { replace: true });
        } else if (tabParam === 'dashboard') {
            setActiveTab(BuyerMarketplaceTab.Opportunities);
            navigate('/opportunities', { replace: true });
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        fetchBuyerProfile();
    }, []);

    const checkBuyerNdaStatus = () => {
        axios
            .get('/api/Marketplace/MarketplaceBuyersNda')
            .then((response) => {
                const status: BuyersNdaStatusResponse = response.data;
                setIsNdaSigned(status.hasSignedLatestNda);
                setIsNdaCheckCompleted(true);
            })
            .catch((error) => {
                console.log(error);
                setIsNdaSigned(false);
                setIsNdaCheckCompleted(true);
            });
    };

    useEffect(() => {
        checkBuyerNdaStatus();
    }, []);

    const handleNdaSigned = () => {
        setIsNdaSigned(true);
    };

    const isTabAvailable = (tab: BuyerMarketplaceTab) => {
        if (tab === BuyerMarketplaceTab.ConnectMarketplaceTemplates) {
            if (summary?.roleId === USER_ROLES.ADMIN) {
                return true;
            }
            const requiredPermissions = {
                isPrimaryBuyer: false,
                type: TeamMemberType.Internal,
                role: InternalTeamMemberRole.CorpDev,
                permission: null,
            } as TeamMemberPermissions;

            const currentPermissions = {
                isPrimaryBuyer:
                    buyerProfile?.teamMemberRole?.isPrimaryBuyer || false,
                type: buyerProfile?.teamMemberRole?.teamMemberType,
                role: buyerProfile?.teamMemberRole?.internalTeamMemberRole,
                permission: null,
            } as TeamMemberPermissions;

            return TeamMemberPermissionsHelper.isAvailable(
                currentPermissions,
                requiredPermissions
            );
        }

        if (summary && summary.roleId === USER_ROLES.PRACTICE_SECONDARY_USER) {
            if (tab === BuyerMarketplaceTab.Profile) {
                const requiredPermissions = {
                    isPrimaryBuyer: false,
                    type: TeamMemberType.Internal,
                    role: null,
                    permission: null,
                } as TeamMemberPermissions;

                const currentPermissions = {
                    isPrimaryBuyer: false,
                    type: buyerProfile?.teamMemberRole?.teamMemberType,
                    role: null,
                    permission: null,
                } as TeamMemberPermissions;

                const result = TeamMemberPermissionsHelper.isAvailable(
                    currentPermissions,
                    requiredPermissions
                );
                return result;
            } else if (
                tab === BuyerMarketplaceTab.CurrentProcessCommercialTerms
            ) {
                const requiredPermissions = {
                    isPrimaryBuyer: false,
                    type: TeamMemberType.Internal,
                    role: InternalTeamMemberRole.CorpDev,
                    permission: null,
                } as TeamMemberPermissions;
                const currentPermissions = {
                    isPrimaryBuyer:
                        buyerProfile?.teamMemberRole?.isPrimaryBuyer,
                    type: buyerProfile?.teamMemberRole?.teamMemberType,
                    role: buyerProfile?.teamMemberRole?.internalTeamMemberRole,
                    permission: null,
                } as TeamMemberPermissions;
                return TeamMemberPermissionsHelper.isAvailable(
                    currentPermissions,
                    requiredPermissions
                );
            } else {
                return (
                    tab === BuyerMarketplaceTab.Opportunities ||
                    tab === BuyerMarketplaceTab.ConnectMarketplaceAgreements
                );
            }
        }
        switch (tab) {
            case BuyerMarketplaceTab.MyTeamMembers:
                return isFF_MrktplcBuyers_3_6Enabled;
            default:
                return true;
        }
    };

    function renderTabContent() {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return (
            activeTabData?.content || (
                <div className="d-flex align-items-center justify-content-center mt-4">
                    <h1>Empty Tab</h1>
                </div>
            )
        );
    }
    const summary = useStateSelector((state) => state.userSummary.summary);

    const getTabNotificationFlag = (tab: number) => {
        const targetCode = tabComponents.find((t) => t.id === tab);
        return targetCode ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={targetCode.notificationFlag}
                    includePrefix={true}
                />
            </>
        ) : (
            <></>
        );
    };

    const renderTabs = () => {
        return (
            <Card className="main-card">
                <div className="body_white_box practice-page-tabs m-0">
                    <div className="body_inner_content">
                        <div className="RRT__container">
                            <div className="RRT__tabs body-tabs">
                                {tabComponents
                                    ?.filter((x) => isTabAvailable(x.id))
                                    .map((item) => {
                                        return (
                                            <div
                                                key={item.id}
                                                className={
                                                    activeTab === item.id
                                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                                        : 'RRT__tab'
                                                }
                                                onClick={async () => {
                                                    setActiveTab(item.id);
                                                    await dispatch(
                                                        RefreshNotificationFlags()
                                                    );
                                                }}>
                                                {item.label}
                                                {getTabNotificationFlag(
                                                    item.id
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </div>
            </Card>
        );
    };

    const renderOpportunityDetails = () => {
        return (
            <OpportunityDetails
                refreshOpportunities={() => {
                    setRefreshOpportunitiesToggle(!refreshOpportunitiesToggle);
                }}
            />
        );
    };

    const renderHeader = (showContactSupport = false) => {
        return (
            <div className="main-title-content">
                <div className="title-breadcrumb">
                    <div>
                        <div className="page-title">
                            <h3>Marketplace Dashboard</h3>
                        </div>
                        <Breadcrumb>
                            <BreadcrumbItem>
                                <HomeButton />
                            </BreadcrumbItem>
                            <BreadcrumbItem active>
                                Marketplace Dashboard
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </div>
                    {showContactSupport && (
                        <div className="buttons-wrapper">
                            <ContactSupportButton
                                supportContext={SupportContextEnum.Marketplace}
                                buttonType="reference"
                                buttonText="Contact Administrator"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderMarketplacePage = () => {
        const isAdmin = summary?.roleId === USER_ROLES.ADMIN;

        if (!isNdaCheckCompleted) {
            return <div className="buyer-marketplace">{renderHeader()}</div>;
        }

        if (!isNdaSigned && !isAdmin) {
            return (
                <div className="buyer-marketplace">
                    {renderHeader()}
                    <Card className="main-card nda-gate-card">
                        <BuyerNdaGate onNdaSigned={handleNdaSigned} />
                    </Card>
                </div>
            );
        }

        return (
            <div className="buyer-marketplace">
                {renderHeader(true)}

                {renderTabs()}
                {selectedOpportunity?.opportunityId > 0 &&
                activeTab === BuyerMarketplaceTab.Opportunities ? (
                    renderOpportunityDetails()
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return <Layout>{renderMarketplacePage()}</Layout>;
};

export default BuyerMarketplace;
